/**
 * Phase 6a — AHU Board web-storage side channels (PHASE6 inventory §23.2/§23.3,
 * superseding the Part-1 §1.12 table). Keys and value formats are kept
 * BYTE-COMPATIBLE with the Backbone board (parity 217) so mixed legacy/React
 * sessions interoperate during migration.
 *
 * 6a implements the READ side the shell needs (+ the symmetric writers the
 * shell owns: pinned-view, color mode, 3d/3d_td). The selection-restore
 * CONSUMER (select:section {from_storage} on boot) is canvas-coupled and lands
 * in 6b/6d — the helpers are ready here.
 */

// ---------------------------------------------------------------------------
// localStorage `canvas_color_mode` — "true"/"false" strings.
// [NEG]#9: legacy reads this via eval(storedValue) (canvas.view3d:114,
// palette.view:19). Replaced with a comparison — behavior-identical for the
// only stored values "true"/"false"; absent key defaults to true (legacy
// `showColor` default).
// ---------------------------------------------------------------------------
export function readCanvasColorMode(): boolean {
  const stored = localStorage.getItem('canvas_color_mode');
  if (stored == null) return true;
  return stored !== 'false';
}

export function writeCanvasColorMode(showColor: boolean): void {
  localStorage.setItem('canvas_color_mode', String(showColor));
}

// ---------------------------------------------------------------------------
// sessionStorage `pinned-view` — keeps the Views toolbar window pinned open
// (MainView pinOrUnpin; truthy check is `!!sessionStorage.getItem(...)`).
// ---------------------------------------------------------------------------
export function readPinnedView(): boolean {
  return !!sessionStorage.getItem('pinned-view');
}

export function writePinnedView(pinned: boolean): void {
  if (pinned) sessionStorage.setItem('pinned-view', 'true');
  else sessionStorage.removeItem('pinned-view');
}

// ---------------------------------------------------------------------------
// localStorage `last_selected_sections` — selection restore across reload
// (§1.12): JSON `[{ahu_id, section_id, tunnel_id}]`. Written by tunnel.js
// visibilitychange + tab.sections check_multiply_list; read/pruned on boot.
// ---------------------------------------------------------------------------
export interface LastSelectedSection {
  ahu_id: number;
  section_id: number;
  tunnel_id: number;
}

export function readLastSelectedSections(): LastSelectedSection[] {
  const raw = localStorage.getItem('last_selected_sections');
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (e): e is LastSelectedSection =>
        !!e && typeof e === 'object' && 'section_id' in e && 'ahu_id' in e
    );
  } catch {
    return [];
  }
}

export function writeLastSelectedSections(entries: LastSelectedSection[]): void {
  if (entries.length) {
    localStorage.setItem('last_selected_sections', JSON.stringify(entries));
  } else {
    localStorage.removeItem('last_selected_sections');
  }
}

export function clearLastSelectedSections(): void {
  localStorage.removeItem('last_selected_sections');
}

// ---------------------------------------------------------------------------
// sessionStorage `visited` — distinguishes first visit vs refresh for the
// selection restore (tunnel.js init, parity 78). Restore applies when the
// navigation is a reload OR the session has already visited the board.
// ---------------------------------------------------------------------------
export function shouldRestoreSelection(): boolean {
  const nav = performance.getEntriesByType('navigation')[0] as
    | PerformanceNavigationTiming
    | undefined;
  return nav?.type === 'reload' || !!sessionStorage.getItem('visited');
}

export function markVisited(): void {
  sessionStorage.setItem('visited', 'true');
}

// ---------------------------------------------------------------------------
// localStorage `3d` / `3d_td` — which CAD variant the legacy /model/view/ page
// opens; MUTUALLY exclusive per AHU (MainView model_btn_click/_3d, parity 28).
// ---------------------------------------------------------------------------
export function selectModel3d(ahuId: number): void {
  if (localStorage.getItem('3d_td') === String(ahuId)) localStorage.removeItem('3d_td');
  localStorage.setItem('3d', String(ahuId));
}

export function selectModel3dTechData(ahuId: number): void {
  if (localStorage.getItem('3d') === String(ahuId)) localStorage.removeItem('3d');
  localStorage.setItem('3d_td', String(ahuId));
}

// ---------------------------------------------------------------------------
// localStorage `returnProjectBoard` — ViewProjectName project-link click stores
// the AHU id so the project board can scroll back to it (§10.1).
// ---------------------------------------------------------------------------
export function writeReturnProjectBoard(ahuId: number): void {
  localStorage.setItem('returnProjectBoard', String(ahuId));
}
